/**
 * The document-preview split panel beside chat (MAR-437 follow-up). Composed by the `web-chat` store
 * facade (MAR-948).
 *
 * It is conversation state, not shell state: it must not survive a conversation switch, and a switch
 * is something only the store knows happened — which is why it is not in `usePanelLayout` beside the
 * law-citation panel it shares a slot with.
 *
 * `version` busts the preview's own fetch cache (`ChatDocPreviewPanel.vue` appends it to the file
 * URL). A document REGENERATED IN PLACE — same item id, new bytes — would otherwise show the stale
 * render forever, since the panel's fetch is keyed by `documentId`. Every open stamps a fresh
 * version, and `bumpIfShowing` stamps another when a draft flush regenerates the document on screen.
 */
import { ref } from 'vue'

/** A route query as the router reads and writes it. */
export type RouteQuery = Record<string, string | null | (string | null)[] | undefined>

/** The two router members this reads — structural, so Nuxt's router and a test stub both fit. */
export interface DocPreviewRoute {
  router: { replace(to: { query: RouteQuery }): unknown }
  route: { query: RouteQuery }
}

export interface DocPreviewDeps {
  /** The router, or `null` where there is none (the hermetic suite builds the store bare). */
  routing: DocPreviewRoute | null
  /** Mutually exclusive with the law-citation panel — both occupy the same slot in `ChatPanel`. */
  closeLaw: () => void
}

export type PreviewDocument = { documentId: string, name: string, version: number }

export function useDocPreview({ routing, closeLaw }: DocPreviewDeps) {
  const previewDocument = ref<PreviewDocument | null>(null)

  /**
   * Mirrors the panel onto `?preview=<id>` so a reload reopens it. `replace`, not `push`: opening a
   * preview is not a navigation the back button should step through.
   *
   * Nothing to write, so write nothing (MAR-465). A `replace` to the CURRENT route is a second
   * navigation, and it cancels any navigation already in flight. `startNewChat()` closes the preview,
   * and the sidebar's Marcus mark calls it from the same click that is navigating to /app — an
   * unconditional replace here silently killed that navigation, and the logo stopped working.
   */
  function syncPreviewQuery(documentId: string | null) {
    if (!routing) return
    const { router, route } = routing
    if ((route.query.preview ?? null) === documentId) return
    const query = { ...route.query }
    if (documentId) query.preview = documentId
    else delete query.preview
    router.replace({ query })
  }

  function openDocPreview(documentId: string, name: string) {
    closeLaw()
    previewDocument.value = { documentId, name, version: Date.now() }
    syncPreviewQuery(documentId)
  }

  function closeDocPreview() {
    previewDocument.value = null
    syncPreviewQuery(null)
  }

  /** The document on screen just got new bytes at the same id — make the panel fetch again. */
  function bumpIfShowing(itemId: string | null) {
    if (itemId && previewDocument.value?.documentId === itemId) {
      previewDocument.value = { ...previewDocument.value, version: Date.now() }
    }
  }

  return { previewDocument, openDocPreview, closeDocPreview, bumpIfShowing }
}
